import { useEffect, useState, useCallback } from "react";

interface ChainSummary {
  chain_name: string;
  chain_id: number;
  total_supply: number;
  circulating_supply: number;
  burned_amount: number;
  holder_count: number;
  last_update: string;
}

interface Totals {
  total_supply: number;
  circulating_supply: number;
  burned_amount: number;
  total_holders: number;
  total_transactions: number;
  locked_amount: number;
}

interface SummaryResponse {
  status: string;
  chains: ChainSummary[];
  totals: Totals;
  timestamp: string;
}

interface DistributionRange {
  range: string;
  holders: number;
  balance: number;
  percentage: number;
}

interface DistributionResponse {
  status: string;
  distribution: Record<string, DistributionRange[]>;
}

interface VelocityData {
  chain_id: number;
  period: string;
  transfers: number;
  volume: number;
  unique_senders: number;
  unique_receivers: number;
  velocity_ratio: number;
  last_update: string;
}

interface VelocityResponse {
  status: string;
  period: string;
  data: VelocityData[];
}

const CHAIN_META: Record<
  string,
  { label: string; color: string; chainId: number }
> = {
  ethereum: { label: "Ethereum", color: "#627EEA", chainId: 1 },
  bsc: { label: "BSC", color: "#F0B90B", chainId: 56 },
  polygon: { label: "Polygon", color: "#8247E5", chainId: 137 },
  tron: { label: "TRON", color: "#FF0013", chainId: 195 },
};

const CHAIN_ID_TO_NAME: Record<number, string> = {
  1: "ethereum",
  56: "bsc",
  137: "polygon",
  195: "tron",
};

function formatNumber(n: number, decimals = 0): string {
  if (n >= 1_000_000_000) return (n / 1_000_000_000).toFixed(1) + "B";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return n.toFixed(decimals);
}

function formatTokens(n: number): string {
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

interface TokenomicsProps {
  refreshInterval?: number;
}

export function Tokenomics({ refreshInterval = 60000 }: TokenomicsProps) {
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [distribution, setDistribution] =
    useState<DistributionResponse | null>(null);
  const [velocity, setVelocity] = useState<VelocityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      const [sumRes, distRes, velRes] = await Promise.all([
        fetch("/api/token-metrics/summary").then((r) => {
          if (!r.ok) throw new Error(`Summary: ${r.status}`);
          return r.json();
        }),
        fetch("/api/token-metrics/distribution").then((r) => {
          if (!r.ok) throw new Error(`Distribution: ${r.status}`);
          return r.json();
        }),
        fetch("/api/token-metrics/velocity").then((r) => {
          if (!r.ok) throw new Error(`Velocity: ${r.status}`);
          return r.json();
        }),
      ]);
      setSummary(sumRes);
      setDistribution(distRes);
      setVelocity(velRes);
      setLoading(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load data");
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, refreshInterval);
    return () => clearInterval(interval);
  }, [loadData, refreshInterval]);

  if (loading && !summary) {
    return <div className="tokenomics loading">Loading tokenomics data...</div>;
  }

  if (error && !summary) {
    return <div className="tokenomics error">{error}</div>;
  }

  const totals = summary?.totals;
  const chains = summary?.chains ?? [];

  return (
    <div className="tokenomics">
      {/* Cross-Chain Summary */}
      <div className="tokenomics-summary">
        <div className="section-header">
          <h3>FCO Token Overview</h3>
          <div className="header-right">
            {summary?.timestamp && (
              <span className="last-updated">
                Updated {timeAgo(summary.timestamp)}
              </span>
            )}
            <button onClick={loadData} className="refresh-btn" title="Refresh">
              &#x21bb;
            </button>
          </div>
        </div>

        <div className="summary-cards">
          <div className="summary-card">
            <span className="label">Total Supply</span>
            <span className="value">{formatNumber(totals?.total_supply ?? 0)}</span>
            <span className="detail">{formatTokens(totals?.total_supply ?? 0)} FCO</span>
          </div>
          <div className="summary-card">
            <span className="label">Circulating Supply</span>
            <span className="value">{formatNumber(totals?.circulating_supply ?? 0)}</span>
            <span className="detail">{formatTokens(totals?.circulating_supply ?? 0)} FCO</span>
          </div>
          <div className="summary-card">
            <span className="label">Burned</span>
            <span className="value accent-red">{formatNumber(totals?.burned_amount ?? 0)}</span>
            <span className="detail">{formatTokens(totals?.burned_amount ?? 0)} FCO</span>
          </div>
          <div className="summary-card">
            <span className="label">Total Holders</span>
            <span className="value">{totals?.total_holders ?? 0}</span>
            <span className="detail">across all chains</span>
          </div>
          <div className="summary-card">
            <span className="label">Total Transactions</span>
            <span className="value">{formatNumber(totals?.total_transactions ?? 0)}</span>
            <span className="detail">transfers recorded</span>
          </div>
        </div>
      </div>

      {/* Per-Chain Cards */}
      <div className="chain-cards-section">
        <h3>Per-Chain Breakdown</h3>
        <div className="chain-cards">
          {chains.map((chain) => {
            const meta = CHAIN_META[chain.chain_name] ?? {
              label: chain.chain_name.toUpperCase(),
              color: "#58a6ff",
              chainId: chain.chain_id,
            };
            const pctOfTotal =
              totals && totals.total_supply > 0
                ? (chain.total_supply / totals.total_supply) * 100
                : 0;

            return (
              <div key={chain.chain_id} className="chain-card">
                <div className="chain-card-header">
                  <div
                    className="chain-badge"
                    style={{ backgroundColor: meta.color }}
                  >
                    {meta.label.substring(0, 3).toUpperCase()}
                  </div>
                  <div className="chain-name-block">
                    <span className="chain-label">{meta.label}</span>
                    <span className="chain-pct">{pctOfTotal.toFixed(1)}% of total</span>
                  </div>
                </div>

                <div className="chain-supply-bar">
                  <div
                    className="chain-supply-fill"
                    style={{
                      width: `${Math.min(pctOfTotal, 100)}%`,
                      backgroundColor: meta.color,
                    }}
                  />
                </div>

                <div className="chain-stats">
                  <div className="chain-stat">
                    <span className="label">Supply</span>
                    <span className="value">{formatTokens(chain.total_supply)}</span>
                  </div>
                  <div className="chain-stat">
                    <span className="label">Circulating</span>
                    <span className="value">{formatTokens(chain.circulating_supply)}</span>
                  </div>
                  <div className="chain-stat">
                    <span className="label">Burned</span>
                    <span className="value">{formatTokens(chain.burned_amount)}</span>
                  </div>
                  <div className="chain-stat">
                    <span className="label">Holders</span>
                    <span className="value">{chain.holder_count}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Holder Distribution */}
      {distribution && distribution.distribution && (
        <div className="distribution-section">
          <h3>Holder Distribution</h3>
          <div className="distribution-table">
            <div className="dist-table-header">
              <span className="dist-range">Range (FCO)</span>
              {Object.keys(distribution.distribution).map((chainId) => {
                const name = CHAIN_ID_TO_NAME[Number(chainId)];
                const meta = name ? CHAIN_META[name] : null;
                return (
                  <span key={chainId} className="dist-chain-col">
                    <span
                      className="dist-chain-dot"
                      style={{ backgroundColor: meta?.color ?? "#58a6ff" }}
                    />
                    {meta?.label ?? `Chain ${chainId}`}
                  </span>
                );
              })}
            </div>

            {["0-100", "100-1000", "1000-10000", "10000+"].map((range) => (
              <div key={range} className="dist-row">
                <span className="dist-range">{range}</span>
                {Object.entries(distribution.distribution).map(
                  ([chainId, ranges]) => {
                    const item = ranges.find((r) => r.range === range);
                    return (
                      <span key={chainId} className="dist-chain-col">
                        <span className="dist-holders">{item?.holders ?? 0} holders</span>
                        <span className="dist-balance">
                          {formatNumber(item?.balance ?? 0)} FCO
                        </span>
                        <span className="dist-pct">
                          ({(item?.percentage ?? 0).toFixed(1)}%)
                        </span>
                      </span>
                    );
                  }
                )}
              </div>
            ))}
          </div>

          {/* Visual distribution bars */}
          <div className="distribution-bars">
            {Object.entries(distribution.distribution).map(
              ([chainId, ranges]) => {
                const name = CHAIN_ID_TO_NAME[Number(chainId)];
                const meta = name ? CHAIN_META[name] : null;
                const totalHolders = ranges.reduce(
                  (s, r) => s + r.holders,
                  0
                );
                if (totalHolders === 0) return null;

                return (
                  <div key={chainId} className="dist-bar-row">
                    <span className="dist-bar-label">
                      {meta?.label ?? `Chain ${chainId}`}
                    </span>
                    <div className="dist-bar-track">
                      {ranges.map((r) => {
                        const pct =
                          totalHolders > 0
                            ? (r.holders / totalHolders) * 100
                            : 0;
                        if (pct === 0) return null;
                        const opacity =
                          r.range === "0-100"
                            ? 0.3
                            : r.range === "100-1000"
                              ? 0.5
                              : r.range === "1000-10000"
                                ? 0.7
                                : 1;
                        return (
                          <div
                            key={r.range}
                            className="dist-bar-segment"
                            style={{
                              width: `${pct}%`,
                              backgroundColor: meta?.color ?? "#58a6ff",
                              opacity,
                            }}
                            title={`${r.range}: ${r.holders} holders (${r.percentage.toFixed(1)}% of supply)`}
                          />
                        );
                      })}
                    </div>
                    <span className="dist-bar-total">{totalHolders}</span>
                  </div>
                );
              }
            )}
          </div>
        </div>
      )}

      {/* Token Velocity */}
      {velocity && velocity.data && (
        <div className="velocity-section">
          <h3>Token Velocity (24h)</h3>
          <div className="velocity-cards">
            {velocity.data.map((v) => {
              const name = CHAIN_ID_TO_NAME[v.chain_id];
              const meta = name ? CHAIN_META[name] : null;
              const hasActivity = v.transfers > 0;

              return (
                <div
                  key={v.chain_id}
                  className={`velocity-card ${hasActivity ? "" : "inactive"}`}
                >
                  <div className="velocity-card-header">
                    <span
                      className="chain-dot"
                      style={{ backgroundColor: meta?.color ?? "#58a6ff" }}
                    />
                    <span className="chain-label">
                      {meta?.label ?? `Chain ${v.chain_id}`}
                    </span>
                    {!hasActivity && (
                      <span className="no-activity-badge">No Activity</span>
                    )}
                  </div>

                  <div className="velocity-stats">
                    <div className="velocity-stat">
                      <span className="label">Transfers</span>
                      <span className="value">
                        {formatNumber(v.transfers)}
                      </span>
                    </div>
                    <div className="velocity-stat">
                      <span className="label">Volume</span>
                      <span className="value">
                        {formatNumber(v.volume)} FCO
                      </span>
                    </div>
                    <div className="velocity-stat">
                      <span className="label">Senders</span>
                      <span className="value">{v.unique_senders}</span>
                    </div>
                    <div className="velocity-stat">
                      <span className="label">Receivers</span>
                      <span className="value">{v.unique_receivers}</span>
                    </div>
                    <div className="velocity-stat highlight">
                      <span className="label">Velocity Ratio</span>
                      <span className="value">
                        {v.velocity_ratio.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
